// Every bank the RBI issues IFSC codes to, and the code each one's IFSCs
// start with.
//
// An IFSC is `AAAA0BBBBBB`: four letters identifying the bank, a reserved `0`,
// then the six-character branch code. The first half is therefore knowable the
// moment the bank is named, which is what lets the form fill it in and leave
// only the branch to type.
//
// The data is Razorpay's `ifsc` package (MIT) — the same list behind
// ifsc.razorpay.com, derived from the RBI's own. `banknames.json` alone is
// imported: the package's other file maps all 170k branches and is a megabyte
// the form has no use for.

import bankNames from "ifsc/src/banknames.json";
import type { DropdownOption } from "../components/ui/CustomDropdown";
import { BANK_NAME_PATTERN } from "../features/brand/validation";

/**
 * The dozen PSU banks left after consolidation, the private banks big enough
 * that most people typing a bank name mean one of these, and the small
 * finance / payments banks and foreign banks with real everyday presence in
 * India. Pinned to the top of the dropdown, in this order, so the branch a
 * customer actually banks with is a click away instead of a scroll through
 * 1,400 co-operative and gramin banks.
 */
const POPULAR_BANK_NAMES = [
  "State Bank of India",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "Bank of India",
  "Indian Bank",
  "Central Bank of India",
  "IDBI",
  "Bank of Maharashtra",
  "Punjab & Sind Bank",
  "UCO Bank",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Indusind Bank",
  "Yes Bank",
  "IDFC FIRST Bank",
  "Federal Bank",
  "South Indian Bank",
  "RBL Bank",
  "Karnataka Bank",
  "Jammu and Kashmir Bank",
  "City Union Bank",
  "Bandhan Bank",
  "AU Small Finance Bank",
  "Equitas Small Finance Bank",
  "Ujjivan Small Finance Bank",
  "ESAF Small Finance Bank",
  "Suryoday Small Finance Bank",
  "Standard Chartered Bank",
  "CITI Bank",
  "Deutsche Bank",
  "Dhanlaxmi Bank",
  "Nainital Bank",
  "Tamilnad Mercantile Bank",
  "Paytm Payments Bank",
  "Airtel Payments Bank",
  "India Post Payments Bank",
  "Fincare Small Finance Bank",
  "Jana Small Finance Bank",
  "North East Small Finance Bank",
  "Shivalik Small Finance Bank",
  "Unity Small finance Bank Limited",
  "Utkarsh Small Finance Bank",
  "Catholic Syrian Bank",
  "DCB Bank",
  "Karur Vysya Bank",
  "Saraswat Co-operative Bank",
] as const;

/**
 * Bank names for the dropdown, in the order they are shown.
 *
 * Deduped: about seventy names in the list are held by more than one code —
 * separate co-operative and gramin banks that genuinely share a name. Left in,
 * each would appear two or three times in the dropdown, and the repeats would
 * be indistinguishable from one another.
 *
 * Filtered against the same pattern the form validates on save: a couple
 * dozen RBI entries carry a leading digit, a trailing space or a comma-joined
 * town ("510 Army Base W/s Credit Co-operative Primary Bank", "Janaseva
 * Sahakari Bank, Pune") that the pattern rejects. Offering those in the
 * dropdown only to reject them on save would be a dead end with no way out,
 * so they never make the list.
 *
 * The popular names are pinned to the top in POPULAR_BANK_NAMES order under a
 * "Popular" heading; the remaining 1,350-odd co-operative and gramin banks
 * follow alphabetically under "All banks".
 */
const allValidNames = Array.from(new Set(Object.values(bankNames))).filter(
  (name) => BANK_NAME_PATTERN.test(name)
);

const popularSet = new Set<string>(POPULAR_BANK_NAMES);

export const BANK_OPTIONS: DropdownOption[] = [
  ...POPULAR_BANK_NAMES.map((name) => ({
    value: name,
    label: name,
    group: "Popular",
  })),
  ...allValidNames
    .filter((name) => !popularSet.has(name))
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name, group: "All banks" })),
];

/**
 * Down to the letters and digits that carry meaning, so "HDFC Bank Ltd.",
 * "hdfc bank" and "HDFC  Bank" all reach the same key.
 */
const normalise = (raw: string) =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(ltd|limited)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Only a *trailing* "bank" is dropped, which is what makes "HDFC Bank" match a
 * typed "HDFC" without turning "Bank of India" into "of India".
 */
const withoutTrailingBank = (key: string) => key.replace(/\s+bank$/, "");

/** Short forms people type that no amount of normalising would reach. */
const ALIASES: Record<string, string> = {
  sbi: "SBIN",
  pnb: "PUNB",
  bob: "BARB",
  boi: "BKID",
  iob: "IOBA",
  kvb: "KVBL",
};

const CODE_BY_NAME = new Map<string, string>();

Object.entries(bankNames).forEach(([code, name]) => {
  // First registration wins, so a short form can never quietly displace the
  // full name of another bank. The list holds a few names twice over, and
  // this is what makes which code they resolve to deterministic.
  [normalise(name), withoutTrailingBank(normalise(name))].forEach((key) => {
    if (key && !CODE_BY_NAME.has(key)) CODE_BY_NAME.set(key, code);
  });
});

// Set last and unconditionally: an alias is a deliberate choice about an
// ambiguous short form, so it outranks whatever the sweep above landed on.
Object.entries(ALIASES).forEach(([key, code]) => CODE_BY_NAME.set(key, code));

/**
 * The four letters an IFSC for this bank begins with, or "" for a name the
 * list does not hold — in which case the form leaves the IFSC field alone and
 * the whole code is typed by hand.
 */
export const ifscPrefixForBank = (bankName: string): string =>
  CODE_BY_NAME.get(normalise(bankName)) ?? "";
